import { toast } from "react-toastify";
import log from "loglevel";
import { history } from "../Redux/store";

// #144 / #336 frontend force-upgrade guard.
// The running client embeds its build hash in REACT_APP_VERSION. Each deploy
// publishes /version.json (served no-cache by the edge Lambda) carrying the
// deployed build hash. We poll it and, on mismatch, FORCE a full reload at a
// safe moment (next route navigation, and on the login/entry path) so a
// long-lived Salesforce session can never get stranded on a stale build. A
// dismissible toast alone is insufficient (the user can ignore it forever), so
// the toast is now only a fallback for the can't-upgrade case below.
//
// Reload-loop guard: before a forced reload we record the TARGET version in
// sessionStorage. If, after reloading, the running build still does not match
// that target (e.g. an intermediate cache is still serving the old index.html),
// we do NOT reload again for the same target -- we fall back to the manual
// toast. A genuinely newer deploy (a different target) clears the guard and is
// honoured.

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const VERSION_TOAST_ID = "new-version-available";
const RELOAD_MARKER = "digadop:versionReloadTarget";
const RUNNING_VERSION = process.env.REACT_APP_VERSION;

let timerId = null;
let historyUnlisten = null;
let pendingUpgrade = null; // the deployed version we still owe a reload for

function isDevelopment() {
  return !!process.env.NODE_ENV && process.env.NODE_ENV.includes("development");
}

function alreadyTriedReloadFor(target) {
  try {
    return window.sessionStorage.getItem(RELOAD_MARKER) === target;
  } catch (err) {
    return false; // sessionStorage unavailable -> treat as not-yet-tried
  }
}

function markReloadFor(target) {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, target);
  } catch (err) {
    /* best effort; if storage is blocked the worst case is one extra reload */
  }
}

function clearReloadMarker() {
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER);
  } catch (err) {
    /* ignore */
  }
}

async function fetchDeployedVersion() {
  // Cache-bust on the query string in addition to no-store so we never read a
  // stale version.json from an intermediate cache.
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`version.json responded ${res.status}`);
  const data = await res.json();
  return data && data.version;
}

function promptReload() {
  if (toast.isActive(VERSION_TOAST_ID)) return; // never stack the prompt
  toast.info("A new version of Digadop is available. Click here to reload.", {
    toastId: VERSION_TOAST_ID,
    autoClose: false,
    closeOnClick: false,
    onClick: () => window.location.reload(),
  });
}

// Force the upgrade now. Loop-guarded: refuses to reload twice for a target the
// previous reload failed to actually deliver.
function performForcedReload(target) {
  if (!target) return;
  if (alreadyTriedReloadFor(target)) {
    // We already reloaded for this target and it did not take. Don't loop --
    // surface a manual reload instead.
    promptReload();
    return;
  }
  markReloadFor(target);
  log.info(`[versionCheck] forcing reload to deployed build ${target}`);
  window.location.reload();
}

async function checkOnce() {
  try {
    const deployed = await fetchDeployedVersion();
    if (!deployed || !RUNNING_VERSION) return;

    if (deployed === RUNNING_VERSION) {
      // In sync (or a forced upgrade just landed): clear any stale guard.
      pendingUpgrade = null;
      clearReloadMarker();
      return;
    }

    // Versions genuinely differ.
    if (alreadyTriedReloadFor(deployed)) {
      // A forced reload to this exact target already happened but the new build
      // is still not being served (edge cache not yet purged). Do NOT loop;
      // offer a manual reload and wait it out.
      log.warn(
        `[versionCheck] running ${RUNNING_VERSION} still != deployed ${deployed} after a forced reload; not looping`
      );
      promptReload();
      return;
    }

    pendingUpgrade = deployed;
    log.info(
      `[versionCheck] deployed ${deployed} differs from running ${RUNNING_VERSION}; scheduling forced upgrade`
    );

    // On the login/entry path, upgrade immediately (no in-progress work to
    // lose). Elsewhere, inform now and force on the next route navigation.
    const path = (window.location && window.location.pathname) || "";
    if (path === "/" || path.indexOf("/login") !== -1) {
      performForcedReload(deployed);
    } else {
      promptReload();
    }
  } catch (err) {
    // A missing or unreachable version.json (e.g. local dev) is non-fatal.
    log.debug("[versionCheck] version probe failed", err);
  }
}

function handleVisibility() {
  if (document.visibilityState === "visible") checkOnce();
}

export function startVersionCheck() {
  if (timerId || isDevelopment()) return; // idempotent + skip dev (no version.json)
  checkOnce();
  timerId = setInterval(checkOnce, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibility);
  // Force the pending upgrade on the next route navigation (a safe moment: the
  // user is between views, so no in-progress work is lost).
  if (history && !historyUnlisten) {
    historyUnlisten = history.listen(() => {
      if (pendingUpgrade) performForcedReload(pendingUpgrade);
    });
  }
}

export function stopVersionCheck() {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
  document.removeEventListener("visibilitychange", handleVisibility);
  if (historyUnlisten) {
    historyUnlisten();
    historyUnlisten = null;
  }
}
